import type { INodeDto } from '@/types/INodeDto';
import { Model } from '../../api/apiClient';
import { InstanceModel } from './InstanceModel';

/**
 * Represents a scene containing multiple model instances.
 * The Scene maintains a shared node array (INodeDto) that all models reference.
 * This is the entry point for our client-side LCAoM (Life-Cycle Assessment object Model)
 */
export class Scene {
  /** Array of InstanceModel objects representing all models in the scene */
  readonly models: InstanceModel[] = [];

  /** Shared array of all nodes (INodeDto) across all models */
  private nodes: INodeDto[] = [];

  /** Map from modelId to InstanceModel for fast lookup */
  private modelMap: Map<string, InstanceModel> = new Map();

  /**
   * Creates a new empty Scene.
   */
  constructor() { }

  /**
   * Gets the shared node array.
   */
  getNodes(): readonly INodeDto[] {
    return this.nodes;
  }

  /**
   * Gets the total number of nodes across all models.
   */
  getTotalNodeCount(): number {
    return this.nodes.length;
  }

  /**
   * Gets the number of models in the scene.
   */
  getModelCount(): number {
    return this.models.length;
  }

  /**
   * Adds a model to the scene.
   * @param instanceModel The InstanceModel to add
   */
  addModel(instanceModel: InstanceModel): void {
    if (this.modelMap.has(instanceModel.modelId)) {
      throw new Error(`Model with id ${instanceModel.modelId} already exists in the scene`);
    }

    this.models.push(instanceModel);
    this.modelMap.set(instanceModel.modelId, instanceModel);
  }

  /**
   * Creates and adds a new InstanceModel from a Model.
   * The InstanceModel will share the scene's node array.
   * @param model The Model metadata
   * @returns The created InstanceModel
   */
  createAndAddModel(model: Model): InstanceModel {
    const instanceModel = new InstanceModel(model, this.nodes);
    this.addModel(instanceModel);
    return instanceModel;
  }

  /**
   * Adds a Model along with its node array to the scene.
   * This is the primary method for adding data from separate API calls.
   *
   * @param model The Model metadata from the models API
   * @param nodes The node array (INodeDto) from the nodes API
   * @returns The created InstanceModel with tree already built
   * 
   * @example
   * ```typescript
   * // Fetch from separate APIs
   * const model = await modelsApi.getModel(modelId);
   * const nodes = await nodesApi.getNodes(modelId);
   * 
   * // Add to scene
   * const instanceModel = scene.addModelWithNodes(model, nodes);
   * 
   * // Now ready to use
   * const rootNodes = instanceModel.getRootNodes();
   * ```
   */
  addModelWithNodes(model: Model, nodes: INodeDto[]): InstanceModel {
    // Add nodes to the shared array
    this.addNodes(nodes);

    // Create and add the InstanceModel
    const instanceModel = this.createAndAddModel(model);

    // Build the tree structure
    instanceModel.buildTree();

    return instanceModel;
  }

  /**
   * Gets an InstanceModel by its modelId.
   */
  getModel(modelId: string): InstanceModel | undefined {
    return this.modelMap.get(modelId);
  }

  /**
   * Removes a model from the scene by its modelId.
   * Also removes the associated node data from the shared nodes array
   * and rebuilds all remaining trees (since indices shift after removal).
   */
  removeModel(modelId: string): boolean {
    const index = this.models.findIndex(m => m.modelId === modelId);
    if (index === -1) {
      return false;
    }

    // Remove the model from the models array and map
    this.models.splice(index, 1);
    this.modelMap.delete(modelId);

    // Remove nodes belonging to this model from the shared array
    this.nodes = this.nodes.filter(node => node.modelId !== modelId);

    // Rebuild all remaining trees since indices have shifted
    this.buildAllTrees();

    return true;
  }

  /**
   * Checks if a model exists in the scene.
   */
  hasModel(modelId: string): boolean {
    return this.modelMap.has(modelId);
  }

  /**
   * Pre-allocates space in the nodes array for better performance with large datasets.
   * Call this before adding nodes if you know the total count ahead of time.
   * @param totalCount The total number of nodes that will be added
   */
  preallocateNodes(totalCount: number): void {
    if (totalCount > this.nodes.length) {
      // Pre-size the array to avoid repeated resizing during adds
      this.nodes.length = totalCount;
      this.nodes.length = 0; // Reset length but keep capacity
    }
  }

  /**
   * Adds nodes to the shared node array.
   * @param nodes Array of nodes (INodeDto) to add
   * @returns The starting index where nodes were added
   */
  addNodes(nodes: INodeDto[]): number {
    const startIndex = this.nodes.length;

    // Direct index assignment is faster than push/concat for large arrays
    // when the array has been pre-allocated or we're adding many items
    const newLength = startIndex + nodes.length;

    // Ensure capacity (this is a hint to the engine)
    if (this.nodes.length < newLength) {
      this.nodes.length = newLength;
    }

    // Copy by index - avoids function call overhead and stack limits
    for (let i = 0; i < nodes.length; i++) {
      this.nodes[startIndex + i] = nodes[i];
    }

    return startIndex;
  }

  /**
   * Sets the entire node array (replaces existing nodes).
   * Use with caution as this will invalidate existing tree structures.
   * @param nodes The new array of nodes (INodeDto)
   */
  setNodes(nodes: INodeDto[]): void {
    this.nodes = nodes;
  }

  /**
   * Builds tree structures for all models in the scene.
   * Call this after all nodes have been loaded.
   */
  buildAllTrees(): void {
    for (const model of this.models) {
      model.buildTree();
    }
  }

  /**
   * Clears all models and nodes from the scene.
   */
  clear(): void {
    this.models.length = 0;
    this.modelMap.clear();
    this.nodes = [];
  }

  /**
   * Gets a node by its index in the shared array.
   */
  getNodeByIndex(index: number): INodeDto | null {
    if (index >= 0 && index < this.nodes.length) {
      return this.nodes[index];
    }
    return null;
  }

  /**
   * Finds all nodes matching a predicate across all models.
   */
  findNodes(predicate: (node: INodeDto) => boolean): INodeDto[] {
    return this.nodes.filter(predicate);
  }

  /**
   * Gets all unique modelIds present in the node array.
   */
  getUniqueModelIds(): string[] {
    const modelIds = new Set<string>();
    for (const node of this.nodes) {
      const id = node.modelId;
      if (id != null && id !== '') {
        modelIds.add(id);
      }
    }
    return Array.from(modelIds);
  }

  /**
   * Creates InstanceModels for all unique modelIds found in the nodes array.
   * Requires that Model metadata is provided for each modelId.
   * @param modelMetadata Map of modelId to Model metadata
   */
  createModelsFromNodes(modelMetadata: Map<string, Model>): void {
    const uniqueModelIds = this.getUniqueModelIds();

    for (const modelId of uniqueModelIds) {
      if (this.hasModel(modelId)) {
        continue; // Skip if already added
      }

      const model = modelMetadata.get(modelId);
      if (model) {
        this.createAndAddModel(model);
      } else {
        console.warn(`No Model metadata found for modelId: ${modelId}`);
      }
    }
  }
}

